import { Session } from 'src_common/sdk/session';
import { SdkCustomerConfig } from 'src/AppState/sdk/staff';
import { AppEnvParamsBrowser } from 'src/utils/appEnvParams';
import { SdkAutotrader } from 'src/domains/sportsbook/trading/sdk/sdkAutotrader';
import {
    buildGradeModel,
    findBodyInLegacy,
    GradeType,
} from 'src/domains/sportsbook/trading/sdk/models/grades/gradeModels';
import {
    OpenapiProxyStaffUpdateSportGradesParamsType,
    OpenapiProxyStaffUpdateSportGradesResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_staff_update_sport_grades';
import { encodeGradeForView } from 'src/domains/sportsbook/trading/sdk/utils/encodeGradeForView';
import {
    convertPriceLaddersToOldModel,
    convertPriceLadderByKeyToOldModel,
    PriceLaddersVieModel,
    PricesViewModel,
    convertOldMasterPriceLadderToNewModel,
} from 'src/domains/sportsbook/trading/sdk/models/priceLadders/priceLaddersModels';
import {
    OpenapiProxyStaffPatchPriceMasterLadderParamsType,
    OpenapiProxyStaffPatchPriceMasterLadderResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_staff_patch_price_master_ladder';
import { OpenapiProxyStaffUpdatePriceLaddersParamsType } from 'src/api_openapi/generated/openapi_proxy_staff_update_price_ladders';
import {
    OpenapiProxyStaffDeletePriceMasterLadderParamsType,
    OpenapiProxyStaffDeletePriceMasterLadderResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_staff_delete_price_master_ladder';
import { apiSportGradesRequest } from 'src/api_openapi/v2/trading/apiSportGrades';
import { apiSportGradesLegacyRequest } from 'src/api_openapi/v2/trading/apiSportGradesLegacy';
import { apiPatchGradesRequest } from 'src/api_openapi/v2/trading/apiPatchGrades';
import { apiGetAllPriceLaddersRequest } from 'src/api_openapi/v2/trading/apiGetAllPriceLadders';
import {
    ApiPatchPriceLadderByKeyParamsType,
    apiPatchPriceLadderByKeyRequest,
} from 'src/api_openapi/v2/trading/apiPatchPriceLadderByKey';
import { apiDeletePriceStepInPriceLadderByKeyRequest } from 'src/api_openapi/v2/trading/apiDeletePriceStepInPriceLadderByKey';
import { apiPatchPriceStepInPriceLadderRequest } from 'src/api_openapi/v2/trading/apiPatchPriceStepInPriceLadder';
import { apiPublishEventSnapshotRequest } from 'src/api_openapi/v2/trading/apiPublishEventSnapshot';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';

export class SdkTrading {
    public readonly autotrader: SdkAutotrader;

    public constructor(
        private readonly session: Session,
        public readonly config: SdkCustomerConfig,
        private readonly envParamsBrowser: AppEnvParamsBrowser
    ) {
        this.autotrader = new SdkAutotrader(session, envParamsBrowser);
    }

    public async getSportsGrades(sportId: string): Promise<Array<GradeType>> {
        const newTradingSportGradesResponse = await apiSportGradesRequest(
            `${this.envParamsBrowser.gatewayUrl}`,
            { sport: sportId },
            {
                authorization: `Bearer ${this.session.currentJwt}`,
            }
        );

        const newTradingSportLegacyGradesResponse = await apiSportGradesLegacyRequest(
            `${this.envParamsBrowser.gatewayUrl}`,
            { sport: sportId },
            {
                authorization: `Bearer ${this.session.currentJwt}`,
            }
        );

        if (newTradingSportGradesResponse.status === 200 && newTradingSportLegacyGradesResponse.status === 200) {
            return await buildGradeModel(newTradingSportGradesResponse.body, newTradingSportLegacyGradesResponse.body);
        }

        if (newTradingSportLegacyGradesResponse.status !== 200) {
            console.error(
                `newTradingSportLegacyGradesResponse response error. Status: ${newTradingSportLegacyGradesResponse.status}`
            );
        }

        if (newTradingSportGradesResponse.status === 400) {
            console.error(
                `newTradingSportGradesResponse response error. Status: ${newTradingSportGradesResponse.status}`
            );
        }
        return [];
    }

    public async updateSportGrade(
        params: OpenapiProxyStaffUpdateSportGradesParamsType
    ): Promise<OpenapiProxyStaffUpdateSportGradesResponse200Type | null> {
        const newTradingSportGradesResponse = await apiSportGradesRequest(
            `${this.envParamsBrowser.gatewayUrl}`,
            { sport: params.sport_id },
            {
                authorization: `Bearer ${this.session.currentJwt}`,
            }
        );

        const newTradingSportLegacyGradesResponse = await apiSportGradesLegacyRequest(
            `${this.envParamsBrowser.gatewayUrl}`,
            { sport: params.sport_id },
            {
                authorization: `Bearer ${this.session.currentJwt}`,
            }
        );

        if (newTradingSportGradesResponse.status === 200 && newTradingSportLegacyGradesResponse.status === 200) {
            const legacyItem = findBodyInLegacy(newTradingSportLegacyGradesResponse.body, params.requestBody);
            if (legacyItem !== null) {
                const response = await apiPatchGradesRequest(
                    `${this.envParamsBrowser.gatewayUrl}`,
                    {
                        sport: params.sport_id,
                        requestBody: legacyItem,
                    },
                    {
                        authorization: `Bearer ${this.session.currentJwt}`,
                        subject: undefined,
                        'X-Forwarded-For': undefined,
                        'pbe-user': undefined,
                        automaticTrigger: undefined,
                    }
                );

                if (response.status === 200) {
                    return {
                        id: await encodeGradeForView(response.body.name),
                        name: response.body.name,
                        displayName: response.body.displayName,
                        displayOrder: 0,
                        defaultLimits: response.body.default,
                        url: 'New trading',
                        // marketLimits: convert_market_limits_from_new(response.body.marketBetLimits, legacyItem.marketBetLimits)
                        // Just for old trading compatibility
                        marketLimits: { check: { liabilityLimit: -1, maxBet: -1, maxWin: -1, maxMultipleBet: -1 } },
                    };
                }
            }
        }
        return null;
    }

    public async getAllPriceLadders(): Promise<PriceLaddersVieModel | null> {
        const newTradingAllPriceLaddersResponse = await apiGetAllPriceLaddersRequest(
            `${this.envParamsBrowser.gatewayUrl}`,
            {},
            {
                authorization: `Bearer ${this.session.currentJwt}`,
            }
        );

        if (newTradingAllPriceLaddersResponse.status === 200) {
            return convertPriceLaddersToOldModel(newTradingAllPriceLaddersResponse.body);
        }

        console.error(
            `newTradingAllPriceLaddersResponse response error. Status: ${newTradingAllPriceLaddersResponse.status}`
        );

        return null;
    }

    public async getPriceLadderByKey(params: ApiPatchPriceLadderByKeyParamsType): Promise<PricesViewModel | null> {
        const patchPriceLadderResponse = await apiPatchPriceLadderByKeyRequest(
            `${this.envParamsBrowser.gatewayUrl}`,
            { priceLadderKey: params.priceLadderKey, requestBody: {} },
            {
                authorization: `Bearer ${this.session.currentJwt}`,
            }
        );

        if (patchPriceLadderResponse.status === 200) {
            return convertPriceLadderByKeyToOldModel(patchPriceLadderResponse.body);
        }

        console.error(`patchPriceLadderResponse response error. Status: ${patchPriceLadderResponse.status}`);

        return null;
    }

    public async patchPriceLadderByKey(
        params: OpenapiProxyStaffUpdatePriceLaddersParamsType
    ): Promise<OpenapiProxyStaffPatchPriceMasterLadderResponse200Type | null> {
        const patchResponse = await apiPatchPriceLadderByKeyRequest(
            `${this.envParamsBrowser.gatewayUrl}`,
            {
                priceLadderKey: params.requestBody.sport_id,
                requestBody: convertOldMasterPriceLadderToNewModel(params),
            },
            {
                authorization: `Bearer ${this.session.currentJwt}`,
            }
        );

        if (patchResponse.status === 200) {
            return convertPriceLadderByKeyToOldModel(patchResponse.body);
        }

        console.error(`apiPatchPriceLadderByKeyRequest response error. Status: ${patchResponse.status}`);

        return null;
    }

    public async deletePriceStepFromPriceLadder(
        params: OpenapiProxyStaffDeletePriceMasterLadderParamsType
    ): Promise<OpenapiProxyStaffDeletePriceMasterLadderResponse200Type | null> {
        const apiDeletePriceStepInPriceLadderByKeyResponse = await apiDeletePriceStepInPriceLadderByKeyRequest(
            `${this.envParamsBrowser.gatewayUrl}`,
            {
                priceLadderKey: 'master',
                decimalPrice: params.requestBody.d,
            },
            {
                authorization: `Bearer ${this.session.currentJwt}`,
            }
        );

        if (apiDeletePriceStepInPriceLadderByKeyResponse.status === 200) {
            return convertPriceLadderByKeyToOldModel(apiDeletePriceStepInPriceLadderByKeyResponse.body);
        }

        console.error(
            `newTradingDeleteStepFromPriceLadder response error. Status: ${apiDeletePriceStepInPriceLadderByKeyResponse.status}`
        );

        return null;
    }

    public async patchPriceStepInPriceLadder(
        params: OpenapiProxyStaffPatchPriceMasterLadderParamsType
    ): Promise<OpenapiProxyStaffPatchPriceMasterLadderResponse200Type | null> {
        const apiDeletePriceStepInPriceLadderByKeyResponse = await apiDeletePriceStepInPriceLadderByKeyRequest(
            `${this.envParamsBrowser.gatewayUrl}`,
            {
                priceLadderKey: 'master',
                decimalPrice: params.requestBody.oldPrice,
            },
            {
                authorization: `Bearer ${this.session.currentJwt}`,
            }
        );

        if (apiDeletePriceStepInPriceLadderByKeyResponse.status === 400) {
            console.error(
                `newTradingDeleteStepFromPriceLadder response error. Status: ${apiDeletePriceStepInPriceLadderByKeyResponse.status}`
            );
        }

        if (apiDeletePriceStepInPriceLadderByKeyResponse.status === 200) {
            const newTradingPatchPriceStepInLadder = await apiPatchPriceStepInPriceLadderRequest(
                `${this.envParamsBrowser.gatewayUrl}`,
                {
                    priceLadderKey: 'master',
                    requestBody: {
                        enabled: true,
                        price: {
                            decimal: params.requestBody.d,
                            fractional: params.requestBody.f,
                        },
                    },
                },
                {
                    authorization: `Bearer ${this.session.currentJwt}`,
                }
            );

            if (newTradingPatchPriceStepInLadder.status === 200) {
                return convertPriceLadderByKeyToOldModel(newTradingPatchPriceStepInLadder.body);
            }

            console.error(
                `newTradingPatchPriceStepInLadder response error. Status: ${newTradingPatchPriceStepInLadder.status}`
            );
        }

        return null;
    }

    public async publishEventSnapshot(eventIdRaw: EventId): Promise<void> {
        const eventId = await eventIdRaw.idv2();
        await apiPublishEventSnapshotRequest(
            `${this.envParamsBrowser.gatewayUrl}`,
            { eventId },
            {
                authorization: `Bearer ${this.session.currentJwt}`,
            }
        );
    }
}
